import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api'
import type { Workspace, CurrencyInfo, ExchangeRate } from '@/types'
import { getCurrencySymbol } from '@/utils/currency'

export const useWorkspaceStore = defineStore('workspace', () => {
  const workspaces = ref<Workspace[]>([])
  const currentWorkspace = ref<Workspace | null>(null)
  const isLoading = ref(false)
  const lastError = ref<any>(null)

  // 货币相关状态
  const supportedCurrencies = ref<CurrencyInfo[]>([])
  const exchangeRates = ref<ExchangeRate[]>([])
  const currenciesLoading = ref(false)
  const ratesLoading = ref(false)

  const currentWorkspaceId = computed(() => currentWorkspace.value?.id)
  
  // 当前工作空间的基准货币
  const currentBaseCurrency = computed(() => {
    return currentWorkspace.value?.base_currency || 'CNY'
  })
  
  // 当前基准货币的符号
  const baseCurrencySymbol = computed(() => {
    return getCurrencySymbol(currentBaseCurrency.value)
  })

  async function fetchWorkspaces(forceSelectDefault = false) {
    // 如果正在加载，避免重复请求
    if (isLoading.value) {
      return
    }

    // 检查是否有认证token，如果没有则直接返回
    const token = localStorage.getItem('access_token')
    if (!token) {
      console.warn('No access token, skipping fetchWorkspaces')
      return
    }

    isLoading.value = true
    lastError.value = null
    try {
      const response = await api.getWorkspaces()
      workspaces.value = response
      lastError.value = null

      // 如果需要强制选择默认工作空间（比如登录后），或者没有当前工作空间
      if (forceSelectDefault || !currentWorkspace.value) {
        if (workspaces.value.length > 0) {
          // 如果强制选择默认，忽略localStorage中的保存值
          if (forceSelectDefault) {
            const defaultWorkspace = workspaces.value.find(w => w.is_default) || workspaces.value[0]
            if (defaultWorkspace) {
              setCurrentWorkspace(defaultWorkspace)
              return response
            }
          } else {
            // 正常流程：优先检查localStorage中保存的工作空间
            const savedWorkspaceId = localStorage.getItem('current_workspace_id')
            if (savedWorkspaceId) {
              const savedWorkspace = workspaces.value.find(w => w.id.toString() === savedWorkspaceId)
              if (savedWorkspace) {
                setCurrentWorkspace(savedWorkspace)
                return response
              }
            }
            // 如果没有保存的工作空间，选择默认或第一个
            const defaultWorkspace = workspaces.value.find(w => w.is_default) || workspaces.value[0]
            if (defaultWorkspace) {
              setCurrentWorkspace(defaultWorkspace)
            }
          }
        }
      }

      return response
    } catch (error: any) {
      lastError.value = error
      // 如果是 401 错误，清理数据并停止后续尝试
      if (error?.response?.status === 401) {
        console.warn('Unauthorized (401) when fetching workspaces, clearing data')
        clearWorkspaceData()
        // 不抛出错误，避免触发重试逻辑
        return []
      }
      throw error
    } finally {
      isLoading.value = false
    }
  }

  function setCurrentWorkspace(workspace: Workspace) {
    if (!workspace || !workspace.id) {
      console.error('[Workspace] Cannot set current workspace: invalid workspace or missing id', workspace)
      return
    }
    currentWorkspace.value = workspace
    localStorage.setItem('current_workspace_id', workspace.id.toString())
  }

  async function createWorkspace(data: { name: string; description?: string; icon_url?: string; is_default?: boolean }) {
    try {
      console.log('[Workspace] Creating workspace:', data)
      const response = await api.createWorkspace(data)
      console.log('[Workspace] Created successfully, response:', JSON.stringify(response, null, 2))
      
      // 验证响应数据
      if (!response || !response.id) {
        console.error('[Workspace] Invalid response - missing id:', response)
        throw new Error('创建工作空间失败：服务器响应无效')
      }
      
      workspaces.value.push(response)
      // 创建成功后，自动设置为当前工作空间
      setCurrentWorkspace(response)
      // 如果设置为默认空间，更新其他空间的is_default状态
      if (data.is_default) {
        workspaces.value.forEach(ws => {
          if (ws.id !== response.id) {
            ws.is_default = false
          }
        })
      }
      // 创建成功后，自动设置为当前工作空间
      setCurrentWorkspace(response)
      return response
    } catch (error: any) {
      console.error('[Workspace] Create failed:', error)
      throw error
    }
  }

  async function updateWorkspace(id: number, data: Partial<Workspace>) {
    try {
      const response = await api.updateWorkspace(id, data)
      const index = workspaces.value.findIndex(w => w.id === id)
      if (index !== -1) {
        workspaces.value[index] = response
        // 如果设置为默认空间，更新其他空间的is_default状态
        if (data.is_default === true) {
          workspaces.value.forEach(ws => {
            if (ws.id !== id) {
              ws.is_default = false
            }
          })
        }
        if (currentWorkspace.value?.id === id) {
          currentWorkspace.value = response
        }
      }
      return response
    } catch (error) {
      throw error
    }
  }

  async function deleteWorkspace(id: number) {
    try {
      await api.deleteWorkspace(id)
      workspaces.value = workspaces.value.filter(w => w.id !== id)

      // 如果删除的是当前工作空间，切换到其他工作空间
      if (currentWorkspace.value?.id === id) {
        const remainingWorkspace = workspaces.value[0]
        if (remainingWorkspace) {
          setCurrentWorkspace(remainingWorkspace)
        } else {
          currentWorkspace.value = null
        }
      }
    } catch (error) {
      throw error
    }
  }

  function clearWorkspaceData() {
    workspaces.value = []
    currentWorkspace.value = null
    localStorage.removeItem('current_workspace_id')
    isLoading.value = false
    lastError.value = null
    // 清理货币相关数据
    supportedCurrencies.value = []
    exchangeRates.value = []
  }

  // 货币相关方法
  async function loadSupportedCurrencies() {
    if (currenciesLoading.value) return
    
    currenciesLoading.value = true
    try {
      const response = await api.getSupportedCurrencies()
      supportedCurrencies.value = response.currencies
    } catch (error) {
      console.error('加载支持的货币列表失败:', error)
      throw error
    } finally {
      currenciesLoading.value = false
    }
  }

  async function loadExchangeRates(baseCurrency?: string) {
    const currency = baseCurrency || currentBaseCurrency.value
    if (ratesLoading.value) return
    
    ratesLoading.value = true
    try {
      const rates = await api.getExchangeRates(currency)
      exchangeRates.value = rates
    } catch (error) {
      console.error('加载汇率失败:', error)
      throw error
    } finally {
      ratesLoading.value = false
    }
  }

  async function updateBaseCurrency(currency: string) {
    if (!currentWorkspace.value) {
      throw new Error('没有当前工作空间')
    }
    
    const workspaceId = currentWorkspace.value.id
    
    try {
      await api.updateWorkspaceBaseCurrency(workspaceId, currency)
      
      // 重新加载工作空间列表
      const response = await api.getWorkspaces()
      workspaces.value = response
      
      // 找到并更新当前工作空间
      const updatedWorkspace = workspaces.value.find(w => w.id === workspaceId)
      if (updatedWorkspace) {
        currentWorkspace.value = updatedWorkspace
        console.log('基准货币已更新:', updatedWorkspace.base_currency)
      }
      
      // 重新加载汇率
      await loadExchangeRates(currency)
    } catch (error) {
      console.error('更新基准货币失败:', error)
      throw error
    }
  }

  async function refreshExchangeRates(force: boolean = false) {
    try {
      const result = await api.refreshExchangeRates(currentBaseCurrency.value, force)
      // 刷新后重新加载汇率
      await loadExchangeRates()
      return result
    } catch (error) {
      console.error('刷新汇率失败:', error)
      throw error
    }
  }

  // 货币转换工具方法
  function convertAmount(amount: number, fromCurrency: string, toCurrency?: string): number {
    const target = toCurrency || currentBaseCurrency.value
    if (fromCurrency === target) return amount
    
    const rate = exchangeRates.value.find(
      r => r.base_currency === fromCurrency && r.target_currency === target
    )
    
    return rate ? amount * rate.rate : amount
  }

  return {
    // 工作空间相关
    workspaces,
    currentWorkspace,
    currentWorkspaceId,
    isLoading,
    lastError,
    fetchWorkspaces,
    setCurrentWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    clearWorkspaceData,
    
    // 货币相关
    supportedCurrencies,
    exchangeRates,
    currenciesLoading,
    ratesLoading,
    currentBaseCurrency,
    baseCurrencySymbol,
    loadSupportedCurrencies,
    loadExchangeRates,
    updateBaseCurrency,
    refreshExchangeRates,
    convertAmount
  }
})

